<template>
  <div
    class="c-book__container"
    ref="bookContainer"
    @click="setIndex($event)"
    @mousedown="removeHighlight()"
    :style="
      !isHTML
        ? `width: ${containerWidth}px; height: ${containerHeight}px`
        : 'width: 100%;'
    "
  >
    <div
      class="c-book__content-loaded"
      v-show="lazyLoadPage"
      :style="
        !isHTML
          ? `transform: scale(${scaleVal}); transform-origin: ${transformOrigin} top 0;`
          : `font-size: ${zoomLevel}%; width: 100%; text-align: left;`
      "
      ref="bookContent"
    >
      <div
        :id="getPageId"
        class="book-page"
        :class="[{ 'is-html': isHTML }]"
        v-html="htmlPage"
      ></div>
      <book-page-enrichments
        class="book-page-enrichments"
        v-if="hasDynamicEnrichments"
        :page-id="getPageId"
        :enrichments="getEnrichmentsObject"
        :key="getPageId"
        @toggle-enrichments="onToggleEnrichments"
      ></book-page-enrichments>
      <book-page-star
        :isActive="isActive"
        :isLeft="isLeft()"
        @star-click="toggleBookmark"
      ></book-page-star>
    </div>
    <div class="c-book__content-loading" v-show="!lazyLoadPage"></div>
    <book-page-context-selection-bar
      ref="annotator"
      :pageId="getPageId"
      @show-lookup="lookUpSelectedText"
    ></book-page-context-selection-bar>
    <book-page-note-editor
      ref="annotator_editor"
      :pageId="getPageId"
    ></book-page-note-editor>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { markWord, unMarkWord } from '@/services/markWord'
import annotator from 'annotator'
import AnnotatorService from '@/services/annotator'
import BookPageStar from '@/components/BookPageStar'
import BookPageEnrichmnets from '@/components/BookPageEnrichments'
import BookPageContextSelectionBar from '@/components/BookPageContextSelectionBar'
import BookPageNoteEditor from '@/components/BookPageNoteEditor'

export default {
  name: 'BookPage',

  props: {
    showBars: Boolean,
    htmlPage: String,
    zoomLevel: Number,
    defaultBookWidth: Number,
    fitToWindow: Boolean,
    bookRender: Object,
    pageIndex: Number,
    bookmarks: Array,
    fetchBookmarks: Function,
    isHTML: Boolean,
    connection: Boolean,
    accessToken: String,
    assetsLoaded: Boolean,
    bookContainerHeight: Number,
  },

  data() {
    return {
      pageFit: 0,
      scaleVal: 1,
      containerWidth: 968,
      containerHeight: 1325,
      transformOrigin: 'center',
      isActive: false,
      scriptsLength: 0,
      scriptsLoadedLength: 0,
      cssLength: 0,
      cssLoadedLength: 0,
      hasDynamicEnrichments: false,
      hasOpenEnrichments: false,
      resizeObserver: null,
    }
  },

  components: {
    'book-page-star': BookPageStar,
    'book-page-context-selection-bar': BookPageContextSelectionBar,
    'book-page-note-editor': BookPageNoteEditor,
    'book-page-enrichments': BookPageEnrichmnets,
  },

  computed: {
    getPageId: function() {
      return 'page_' + (parseInt(this.pageIndex) + 1)
    },

    getEnrichmentsObject() {
      if (this.hasDynamicEnrichments) {
        return {
          ...this.bookRender.dynamicEnrichments[this.pageNumber],
          extraURL: this.bookRender.getDynamicEnrichmentsPageUrl(
            this.pageNumber,
          ),
        }
      }

      return {}
    },

    loadedAssets() {
      return this.scriptsLoadedLength + this.cssLoadedLength
    },

    assetsLength() {
      return this.scriptsLength + this.cssLength
    },

    lazyLoadPage() {
      // If book is HTML or internet connection breaks, hide lazyLoader
      if (this.isHTML || !this.connection) {
        return true
      }

      return this.assetsLoaded
    },

    pageNumber() {
      if (this.bookRender.twoPages) {
        return !this.pageIndex
          ? this.bookRender.currentPageNumber
          : this.bookRender.secondPageNumber
      }

      return this.bookRender.currentPageNumber
    },

    ...mapState('bookMenu', {
      highlightSearchWord: state => state.highlightSearchWord,
    }),

    ...mapState('userSettings', {
      responsive: state => state.responsive,
    }),
  },

  watch: {
    zoomLevel() {
      if (!this.isHTML) {
        this.setContainerWidth()
        this.setContainerHeight()
      }

      this.getZoomLevel()
    },

    fitToWindow: {
      handler(val, oldVal) {
        if (val && val !== oldVal) {
          this.calculateFittingZoom()
        }
      },
    },
    bookContainerHeight: {
      handler(val, oldVal) {
        if (this.fitToWindow && val && val !== oldVal) {
          //if increase of width would be small don't do fitting - probably horizontal scroll bar appeared and would loop
          if (val > oldVal && val - oldVal < 20) return
          this.calculateFittingZoom()
        }
      },
    },

    loadedAssets(val) {
      if (val >= this.assetsLength) {
        this.$emit('assets-loaded')
      }
    },

    pageFit() {
      if (!this.isHTML) {
        this.setContainerWidth()
        this.setContainerHeight()
      }
      if (this.fitToWindow) {
        this.calculateFittingZoom()
      } else {
        this.getZoomLevel()
      }
    },

    bookmarks: function() {
      this.highlight()
    },

    htmlPage: function() {
      this.highlight()
    },

    bookRender: function() {
      this.highlight()
    },

    highlightSearchWord(val) {
      if (val) {
        this.highlightSearch()
      }
    },

    connection(state, oldState) {
      if (!oldState && !this.assetsLoaded) {
        this.getScripts()

        /* New Stunt is loading css through scripts */
        if (!this.lazyLoadPage) {
          this.getCSS()
        }
      }
    },

    responsive(val) {
      this.setContainerWidth()
      this.setContainerHeight()
    },
  },

  async mounted() {
    this.checkIfHasDynamicEnrichments(this.pageNumber)

    if (annotator && annotator.ui) {
      annotator.ui.adder.Adder.template = this.$refs.annotator.$el
      annotator.ui.widget.Widget.options.appendTo = this.$refs.bookContainer
      annotator.ui.editor.Editor.template = this.$refs.annotator_editor.$el
    }

    if (this.highlightSearchWord) {
      this.highlightSearch()
    }

    if (!this.isHTML) {
      this.setContainerWidth()
      this.setContainerHeight()
    }

    this.getZoomLevel()
    this.highlight()

    await this.annotate(this.pageNumber)

    if (this.isHTML) {
      this.enableNavHTMLBook()
    }

    this.getScripts()

    /* New Stunt is loading css through scripts */
    if (!this.lazyLoadPage) {
      this.getCSS()
    }

    if (this.responsive !== 'desktop') {
      window.oncontextmenu = e => {
        e.preventDefault()
      }
    }

    this.replaceHexLink()
    this.externalLinks()

    this.resizeObserver = new ResizeObserver(this.onResize)
    this.resizeObserver.observe(this.$refs.bookContent)
    this.media(false)
  },

  updated() {
    if (!this.isHTML) {
      this.setContainerWidth()
      this.setContainerHeight()
    }

    this.getZoomLevel()

    if (this.isHTML) {
      this.enableNavHTMLBook()
    }
  },

  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    this.media(true)
  },

  methods: {
    onResize() {
      this.pageFit = this.$refs.bookContent.offsetHeight
    },
    setIndex(e) {
      // Needs to handle z-index to handle stacking context
      if (!this.bookRender.twoPages) return
      this.$refs.bookContent.style.zIndex = 1
      Array.from(document.querySelectorAll('.c-book__content-loaded')).forEach(
        el => {
          if (el !== this.$refs.bookContent) {
            el.style.zIndex = 0
          }
        },
      )
    },

    onToggleEnrichments(isOpen) {
      this.hasOpenEnrichments = isOpen
    },
    checkIfHasDynamicEnrichments(page) {
      if (this.bookRender.hasDynamicEnrichments(page)) {
        this.hasDynamicEnrichments = true
      }
    },

    media(destroy) {
      const mediaEl = document.querySelectorAll('.ed-media')
      const mediaPlayer = document.querySelector(
        `#${this.getPageId} #custom-player`,
      )

      if (mediaEl.length && mediaPlayer) {
        mediaEl.forEach(el => {
          if (destroy) {
            el.removeEventListener('click', () => {
              mediaPlayer.style.left = el.style.left
              mediaPlayer.style.top = `${parseInt(el.style.top) - 65}px`
            })

            return
          }

          el.addEventListener('click', () => {
            mediaPlayer.style.left = el.style.left
            mediaPlayer.style.top = `${parseInt(el.style.top) - 65}px`
          })
        })
      }
    },

    highlightSearch() {
      markWord(this.highlightSearchWord)
    },

    removeHighlight() {
      unMarkWord()
    },

    enableNavHTMLBook() {
      document.querySelectorAll('.hb-internal-link').forEach(link => {
        if (link === document.querySelector('.book-page .next-chapter a')) {
          link.addEventListener('click', e => {
            e.preventDefault()

            this.bookRender.getNextPage()
          })
        } else {
          link.addEventListener('click', e => {
            e.preventDefault()

            this.bookRender.gotoPage(link.getAttribute('data-page'))
          })
        }
      })
    },

    replaceHexLink() {
      let links = document.querySelectorAll('a[href^="#pf"]'),
        url,
        hex,
        dec

      if (links.length) {
        links.forEach(link => {
          url = link.href
          hex = url.match(new RegExp('#pf' + '(.*)'))[1]
          dec = parseInt(hex, 16)
          link.href = `${window.location.origin}/#/book/${this.$route.params.id}/${dec}`
        })
      }
    },

    externalLinks() {
      let links = document.querySelectorAll('a[href*="referrer=dbook"]')

      links.forEach(link => {
        link.setAttribute('target', '_blank')
      })
    },

    getScripts() {
      const docScripts = document.querySelectorAll('.book-page script')
      let scriptsToLoadLength = 0

      for (let script in docScripts) {
        if (script.src) scriptsToLoadLength = scriptsToLoadLength + 1
      }

      this.scriptsLength = scriptsToLoadLength

      for (let i = 0; i < docScripts.length; i++) {
        const scriptTag = document.createElement('script')
        const target = document.querySelector('.c-book__assets')

        scriptTag.setAttribute('type', 'text/javascript')
        scriptTag.innerHTML = docScripts[i].innerHTML

        if (docScripts[i].src) {
          scriptTag.src = docScripts[i].src

          scriptTag.addEventListener('load', () => {
            this.scriptsLoadedLength = this.scriptsLoadedLength + 1
          })
        }

        target.append(scriptTag)
      }
    },

    getCSS() {
      const docStyles = document.querySelectorAll(
        '.book-page link[rel="stylesheet"]',
      )

      this.cssLength = docStyles.length

      for (let i = 0; i < docStyles.length; i++) {
        const stylesheetTag = document.createElement('link')
        const target = document.querySelector('.c-book__assets')

        stylesheetTag.href = docStyles[i].href
        stylesheetTag.rel = 'stylesheet'
        stylesheetTag.type = 'text/css'

        // extra.css is missing in some hpubs
        if (stylesheetTag.href.match(/extra\.css$/gi)) {
          this.cssLoadedLength = this.cssLoadedLength + 1
        }

        stylesheetTag.addEventListener('load', () => {
          this.cssLoadedLength = this.cssLoadedLength + 1
        })

        target.append(stylesheetTag)
      }
    },

    calcZoom() {
      let zoom = (this.zoomLevel * 0.01).toFixed(2)

      return zoom
    },

    calculateFittingZoom() {
      if (this.$refs.bookContent) {
        let maxHeight = this.bookContainerHeight - 15
        if (this.hasDynamicEnrichments) {
          maxHeight -= 5
        }
        const fittingZoom = 100 * (maxHeight / this.pageFit).toFixed(2)
        this.$emit('fit-zoom-level', parseInt(fittingZoom))
      }
    },

    setContainerWidth() {
      const zoom = this.calcZoom()
      const containerWidth = this.defaultBookWidth * zoom

      this.containerWidth = containerWidth
      this.$emit('page-container-width', this.containerWidth)
    },

    setContainerHeight() {
      if (!this.$refs.bookContent) return
      const clientContent = this.$refs.bookContent.getBoundingClientRect()

      this.containerHeight = clientContent.height
      this.$emit('page-container-height', this.containerHeight)
    },

    getZoomLevel() {
      const zoom = this.calcZoom()

      zoom > 1
        ? (this.transformOrigin = 'center')
        : (this.transformOrigin = 'left')

      this.scaleVal = zoom
    },

    async annotate(pageNo) {
      await AnnotatorService.init({
        element: document.querySelector(`#${this.getPageId}`),
        bookId: this.$route.params.id,
        page: pageNo,
        accessToken: this.accessToken,
      })
    },

    ...mapActions({
      setZoomLevel: 'userSettings/setZoomLevel',
      addUserBookmark: 'bookmarks/addUserBookmark',
      deleteUserBookmark: 'bookmarks/deleteUserBookmark',
    }),

    isLeft() {
      if (
        this.pageIndex === 0 &&
        this.bookRender.twoPages &&
        this.bookRender.currentPageNumber !== 1 &&
        this.bookRender.secondPageNumber
      ) {
        return true
      } else {
        return false
      }
    },

    async toggleBookmark() {
      let currentPageNumber = this.bookRender.currentPageNumber
      let secondPageNumber = this.bookRender.secondPageNumber
      let bookmarks = this.bookmarks
      let pageNumber = null
      let bookmarkId = null

      if (this.bookRender.twoPages) {
        if (this.pageIndex === 0) {
          for (let i in bookmarks) {
            if (bookmarks[i].page === currentPageNumber) {
              bookmarkId = bookmarks[i].id
            }
          }
          if (bookmarkId === null) {
            pageNumber = currentPageNumber
          }
        }
        if (this.pageIndex === 1) {
          for (let i in bookmarks) {
            if (bookmarks[i].page === secondPageNumber) {
              bookmarkId = bookmarks[i].id
            }
          }
          if (bookmarkId === null) {
            pageNumber = secondPageNumber
          }
        }
      } else {
        for (let i in bookmarks) {
          if (bookmarks[i].page === currentPageNumber) {
            bookmarkId = bookmarks[i].id
          }
        }
        if (bookmarkId === null) {
          pageNumber = currentPageNumber
        }
      }

      if (pageNumber) {
        await this.addUserBookmark(pageNumber)
        await this.fetchBookmarks()
        this.highlight()
      }
      if (bookmarkId) {
        await this.deleteUserBookmark(bookmarkId)
        await this.fetchBookmarks()
        this.highlight()
      }
    },

    highlight() {
      let currentPageNumber = this.bookRender.currentPageNumber
      let secondPageNumber = this.bookRender.secondPageNumber
      let bookmarks = this.bookmarks
      this.isActive = false

      if (this.bookRender.twoPages) {
        for (let i in bookmarks) {
          if (bookmarks[i].page === currentPageNumber && this.pageIndex === 0) {
            this.isActive = true
          }
          if (bookmarks[i].page === secondPageNumber && this.pageIndex === 1) {
            this.isActive = true
          }
        }
      } else {
        for (let i in bookmarks) {
          if (bookmarks[i].page === currentPageNumber && this.pageIndex === 0) {
            this.isActive = true
          }
        }
      }
    },

    lookUpSelectedText() {
      this.term = window
        .getSelection()
        .toString()
        .toLowerCase()
      this.$emit('lookup-term', this.term)
    },
  },
}
</script>

<style lang="scss">
@import '@/assets/scss/_vars.scss';

.c-book__container {
  margin: 0 auto;
  display: inline-block;
  direction: ltr;
}

.c-book__content-loaded {
  position: relative;
  overflow: visible;
  display: inline-block;
  vertical-align: top;
  margin-bottom: 0;
  user-select: text;
  background: $white;

  ::selection {
    background-color: $light-purple;
  }
}

.book-page,
.book-page-enrichments {
  &:not(.is-html) {
    width: $default-book-width;
    height: auto;
    text-align: left;
  }
}

span.annotator-hl.annotator-note {
  background: $violet;
}
.annotator-hl {
  cursor: pointer;
}
.annotator-viewer {
  display: none !important;
}
.annotator-adder a,
.annotator-adder button {
  text-indent: 0;
  height: auto;
  width: auto;
  color: #fff;
  padding: 10px;
  display: inline-block;
  font-size: 15px;
  line-height: 15px;
  vertical-align: top;
  margin-bottom: 0px;
}
.annotator-adder a:hover,
.annotator-adder button:hover {
  background: #333;
}
.icon.bookmark {
  position: absolute;
  background: url(/static/images/icons-sprite.svg) no-repeat 0 -98px;
  background-size: 103px 1192px;
  top: 65px;
  z-index: 999;
  height: 25px;
  width: 25px;
  cursor: pointer;
}
.icon.bookmark:hover,
.icon.bookmark.left.active-left,
.icon.bookmark.right.active-right {
  background-position: -77px -98px;
}
.icon.bookmark:focus {
  outline: thin dotted #e55428;
  outline: 4px auto -webkit-focus-ring-color;
  outline-offset: -2px;
}
.icon.bookmark.top {
  top: 17px;
}
.icon.bookmark.left {
  left: 1%;
}
.icon.bookmark.right {
  right: 1%;
}
#multimediaEnricher .drag {
  visibility: visible !important;
}

#custom-player {
  display: none;

  width: 350px;
  height: 60px;

  border: none;
  border-radius: 20px;
  box-shadow: none;

  background: $blue;

  &.visible {
    display: block;

    .cp-action {
      display: flex;
      align-items: center;
      justify-content: center;

      border: none;
      height: 60px;

      .cp-button {
        position: relative;
        top: auto;
        left: auto;
        margin: auto;

        width: 32px;
        height: 32px;

        background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Cpath fill='%23FFF' fill-rule='evenodd' d='M25 16L7 25V7z'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: center center;
        background-size: cover;

        &.pause {
          background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Cpath d='M9 6h2v20H9zm12 0h2v20h-2z' fill='none' fill-rule='evenodd' stroke='%23FFF' stroke-width='2'/%3E%3C/svg%3E");
        }
      }
    }

    .close {
      right: -11px;

      background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M0 0h32v32H0z'/%3E%3Cg stroke='%23383C61' transform='translate(5 5)'%3E%3Ccircle cx='11' cy='11' r='11' fill='%23FFF' stroke-width='2'/%3E%3Cpath d='M15.243 6.05l.707.707-9.193 9.193-.707-.707z'/%3E%3Cpath d='M6.05 6.757l.707-.707 9.193 9.193-.707.707z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    }

    .cp-equalizer {
      background: none;

      width: 250px;
      margin: 19px 22px 0 0;

      &:before {
        content: '';
        position: absolute;
        top: 8px;
        left: 2px;
        right: 0;
        height: 6px;

        background: $white;
        border-radius: 3px;
      }

      .cp-line {
        width: 100%;
      }

      .cp-progress {
        height: 6px;

        background: $orange;
        border-radius: 3px;
      }

      .cp-handle {
        top: 1px;
        width: 20px;
        height: 20px;

        background: $blue;
        border-radius: 50%;

        &:before {
          content: '';
          position: absolute;
          width: 20px;
          height: 20px;
          left: -4px;

          background: $blue;
          border-radius: 50%;
        }

        &:after {
          content: '';
          position: absolute;
          width: 16px;
          height: 16px;
          top: 2px;

          background: $orange;
          border-radius: 50%;
        }
      }
    }
  }
}

.c-book__content-loading {
  position: relative;
  display: inline-block;

  vertical-align: middle;
  background-color: $gradient-blue;
  width: 100%;
  padding-top: 75%;

  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transform: translateX(-100%);
    background-image: linear-gradient(
      90deg,
      rgba(#fff, 0) 0,
      rgba(#fff, 0.2) 20%,
      rgba(#fff, 0.5) 60%,
      rgba(#fff, 0)
    );
    animation: shimmer 4s infinite;
  }

  @keyframes shimmer {
    100% {
      transform: translateX(100%);
    }
  }
}
</style>
